import { Character, CharacterSprite, CharacterAnimator, CharacterController, CharacterMetadata, CharacterShadow, OptimizedAtlasLoader } from '../character/index.js';
import { PathFollower } from '../pathfinding/index.js';
import NPCBehavior from './NPCBehavior.js';
import CombatStats from '../combat/CombatStats.js';
import SeededRandom from '../utils/SeededRandom.js';
import Debug from '../utils/Debug.js';
import { loadManifest as loadLPCManifest } from '../character/LPCLayerDefinitions.js';
import { ITEM_DEFS } from '../loot/ItemDefinitions.js';

/**
 * Character animation configs — maps generic action names to actual animation names.
 * Same configs as in main.js for player characters.
 */
/**
 * Default sprite offset — matches CharacterDebugVisualizer defaults.
 * y: vertical offset to align feet with ground, scale: size multiplier.
 * Override per model if a character needs different values.
 */
const DEFAULT_SPRITE_OFFSET = { x: 0, y: -0.88, z: 0, scale: 1.4 };

/** Sprite offset for LPC-composited characters (64×64 frames, different anchor) */
const LPC_SPRITE_OFFSET = { x: 0, y: -0.6, z: 0, scale: 1.4 };

/** Animation config for all LPC characters (standardized LPC animation names) */
const LPC_NPC_CONFIG = {
  idle: 'breathing-idle',
  walk: 'walking-8-frames',
  run: 'running-8-frames',
  jump: 'jumping-2',
  punch: 'weapon-slash',
  kick: 'weapon-thrust',
  special: 'weapon-shoot',
  spriteOffset: LPC_SPRITE_OFFSET
};

/** LPC animation names needed for metadata */
const LPC_ANIMATION_NAMES = [
  'breathing-idle', 'walking-8-frames', 'running-8-frames', 'jumping-2',
  'weapon-slash', 'weapon-slash-reverse', 'weapon-thrust',
  'weapon-backslash', 'weapon-halfslash', 'weapon-shoot',
  'taking-punch', 'falling-back-death'
];

const CHARACTER_CONFIGS = {
  boy: {
    idle: 'fight-stance-idle-8-frames',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'hurricane-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  mage: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-1',
    punch: 'lead-jab',
    kick: 'roundhouse-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  merchant: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'leg-sweep',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  surfer: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-1',
    punch: 'cross-punch',
    kick: 'flying-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  bear: {
    idle: 'idle-long',
    walk: 'walk-6-frames',
    run: 'running-6-frames',
    jump: 'jump',
    punch: 'going-to-sleep',
    kick: 'going-to-sleep',
    special: 'going-to-sleep',
    spriteOffset: { x: 0, y: -1.12, z: 0, scale: 1.4 }
  },
  hellrat: {
    idle: 'idle',
    walk: 'walk-6-frames',
    run: 'running-6-frames',
    jump: 'jump',
    punch: 'eating',
    kick: 'eating',
    special: 'eating',
    spriteOffset: { x: 0, y: -1.42, z: 0, scale: 1.4 }
  },
  rogue: {
    idle: 'fight-stance-idle-8-frames',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'high-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  }
};

/**
 * NPCManager - Manages NPC lifecycle: spawning, despawning, atlas caching, serialization.
 */
export default class NPCManager {
  /**
   * @param {Object} options
   * @param {World} options.world
   * @param {PathFinder} options.pathFinder
   * @param {CharacterCollisionSystem} options.collisionSystem
   * @param {GroundDetector} options.groundDetector
   */
  constructor(options = {}) {
    this.world = options.world;
    this.pathFinder = options.pathFinder;
    this.collisionSystem = options.collisionSystem;
    this.groundDetector = options.groundDetector;

    /** @type {Map<string, {character: Character, behavior: NPCBehavior}>} */
    this.npcs = new Map();

    /** @type {Map<string, {metadata: CharacterMetadata, atlas: Object, config: Object}>} */
    this._atlasCache = new Map();

    this._nextId = 1;

    // Callback for network sync (fires on NPC spawn/remove)
    this.onMutation = null;

    // Suppresses onMutation during bulk loads and remote ops (prevents re-broadcast loops)
    this._suppressMutation = false;

    /** @type {DialogManager|null} */
    this.dialogManager = null;
  }

  /**
   * Set the dialog manager for NPC interaction.
   * @param {DialogManager} dialogManager
   */
  setDialogManager(dialogManager) {
    this.dialogManager = dialogManager;
  }

  /**
   * Get available character names
   * @returns {string[]}
   */
  static getCharacterNames() {
    return Object.keys(CHARACTER_CONFIGS);
  }

  /**
   * Load or retrieve cached atlas/metadata for a character name
   * @param {string} characterName
   * @returns {Promise<{metadata, atlas, config}>}
   * @private
   */
  async _loadCharacterAssets(characterName) {
    if (this._atlasCache.has(characterName)) {
      return this._atlasCache.get(characterName);
    }

    const characterPath = `/characters/${characterName}`;
    const metadata = await CharacterMetadata.load(characterPath);
    const atlas = await OptimizedAtlasLoader.load(characterPath, metadata);
    const config = CHARACTER_CONFIGS[characterName];

    if (!config) {
      throw new Error(`No config found for character: ${characterName}`);
    }

    const assets = { metadata, atlas, config, characterPath };
    this._atlasCache.set(characterName, assets);
    Debug.log('NPC', `Cached assets for ${characterName}`);
    return assets;
  }

  /**
   * Create a synthetic CharacterMetadata for LPC characters (no network fetch needed).
   * @returns {Object} Metadata-compatible object with spriteWidth, spriteHeight, animationNames
   * @private
   */
  static _createLPCMetadata() {
    return {
      basePath: '/lpc',
      id: 'lpc',
      name: 'LPC Character',
      spriteWidth: 64,
      spriteHeight: 64,
      directions: 4,
      view: 'top-down',
      rotations: {},
      animations: {},
      animationNames: LPC_ANIMATION_NAMES,
      getAnimationFrames: () => [],
      getFrameCount: () => 0,
      hasAnimation: (name) => LPC_ANIMATION_NAMES.includes(name)
    };
  }

  /**
   * Spawn an NPC in the world
   * @param {Object} npcConfig
   * @param {string} [npcConfig.id] - Unique ID (auto-generated if omitted)
   * @param {string} [npcConfig.characterName] - Character type (boy, mage, etc.) — legacy path
   * @param {Object} [npcConfig.lpcAppearance] - LPC appearance config — LPC path
   * @param {Object} [npcConfig.lpcEquipment] - Map of slot → itemId for LPC equipment
   * @param {string} [npcConfig.displayName] - Custom display name for nameplate
   * @param {number} npcConfig.x - World X position
   * @param {number} npcConfig.y - World Y position
   * @param {number} npcConfig.z - World Z position
   * @param {'wander'|'patrol'} [npcConfig.behaviorType='wander']
   * @param {number} [npcConfig.wanderRadius=5]
   * @param {{x,y,z}[]} [npcConfig.patrolPoints=[]]
   * @returns {Promise<Character>}
   */
  async spawnNPC(npcConfig) {
    // Prevent duplicate spawns (e.g. from re-broadcast loops)
    if (npcConfig.id && this.npcs.has(npcConfig.id)) {
      Debug.log('NPC', `NPC "${npcConfig.id}" already exists, skipping spawn`);
      return this.npcs.get(npcConfig.id).character;
    }

    const isLPC = !!npcConfig.lpcAppearance;
    let metadata, atlas, config, characterPath;

    if (isLPC) {
      // LPC path: synthetic metadata, no pre-built atlas (compositor provides it)
      await loadLPCManifest();
      metadata = NPCManager._createLPCMetadata();
      atlas = null;
      config = { ...LPC_NPC_CONFIG };
      characterPath = '/lpc';

      // Override punch/kick with weapon-specific attack animations
      const weaponId = npcConfig.lpcEquipment?.mainhand;
      const weaponDef = weaponId && ITEM_DEFS[weaponId];
      if (weaponDef?.attackAnims) {
        config.punch = weaponDef.attackAnims[0];
        config.kick = weaponDef.attackAnims[1] || weaponDef.attackAnims[0];
        // For ranged weapons (bow/slingshot), also update special
        if (weaponDef.projectileType) {
          config.special = weaponDef.attackAnims[0];
        }
      }
    } else {
      // Legacy path: load pre-built atlas from /characters/{name}/
      ({ metadata, atlas, config, characterPath } = await this._loadCharacterAssets(npcConfig.characterName));
    }

    const id = npcConfig.id || `npc_${this._nextId++}`;

    // Keep _nextId ahead of any explicitly-provided numeric IDs (e.g. from saved worlds)
    if (npcConfig.id) {
      const match = npcConfig.id.match(/^npc_(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10) + 1;
        if (num > this._nextId) this._nextId = num;
      }
    }

    // Create character entity
    const character = new Character({
      id,
      x: npcConfig.x,
      y: npcConfig.y,
      z: npcConfig.z
    });
    character.isNPC = true;
    character.isLPC = isLPC;
    character.metadata = metadata;
    character.characterPath = characterPath;
    character.characterName = npcConfig.characterName || 'lpc';
    character.config = config;
    character.animations = {
      idle: config.idle,
      walk: config.walk,
      run: config.run
    };

    // Display name (works for both LPC and legacy NPCs)
    character.displayName = npcConfig.displayName || null;

    // Store LPC data for serialization and loot drops
    if (isLPC) {
      character.lpcAppearance = npcConfig.lpcAppearance;
      character.lpcEquipment = npcConfig.lpcEquipment || {};
    }

    // Sprite
    const sprite = new CharacterSprite(character, {
      spriteWidth: metadata.spriteWidth,
      spriteHeight: metadata.spriteHeight,
      scale: 2
    });
    sprite.setCollisionSystem(this.collisionSystem);

    if (isLPC) {
      // Hide sprite until LPC compositor delivers the atlas
      sprite.element.style.visibility = 'hidden';
      // Auto-set LPC offset when atlas arrives
      sprite.onAtlasChange = (newAtlas) => {
        if (newAtlas._isLPC) {
          sprite.spriteOffset = { ...LPC_SPRITE_OFFSET };
          sprite.element.style.visibility = '';
        }
      };
      // Set appearance and trigger async composition
      sprite.setLPCAppearance(npcConfig.lpcAppearance);
      const equippedItemDefs = NPCManager._resolveEquipment(npcConfig.lpcEquipment);
      sprite.updateEquipment(equippedItemDefs);
    } else {
      sprite.setAtlas(atlas);
      sprite.spriteOffset = config.spriteOffset
        ? { ...config.spriteOffset }
        : { ...DEFAULT_SPRITE_OFFSET };
    }
    character.sprite = sprite;

    // Animator
    const animator = new CharacterAnimator(character, metadata);
    if (atlas) animator.setAtlas(atlas);
    character.animator = animator;
    animator.setState(config.idle);
    animator.onDirectionChange('south');

    // Controller — no inputManager for NPCs, physics only
    const controller = new CharacterController(character, {
      inputManager: null,
      camera: null,
      collisionSystem: this.collisionSystem,
      groundDetector: this.groundDetector
    });
    character.controller = controller;

    // Shadow
    const shadow = new CharacterShadow(character, this.collisionSystem);
    character.shadow = shadow;

    // PathFollower — no onRepathRequest for NPCs.
    // When stuck/fallen, PathFollower stops and NPCBehavior picks a new safe destination.
    const pathFollower = new PathFollower(character, {
      collisionSystem: this.collisionSystem
    });
    character.pathFollower = pathFollower;

    // Behavior — use saved spawnPoint (synced NPCs) or current position (newly placed)
    const spawnPoint = npcConfig.spawnPoint || { x: npcConfig.x, y: npcConfig.y, z: npcConfig.z };
    const behavior = new NPCBehavior(character, {
      type: npcConfig.behaviorType || 'wander',
      pathFinder: this.pathFinder,
      spawnPoint,
      wanderRadius: npcConfig.wanderRadius || NPCBehavior.DEFAULT_WANDER_RADIUS,
      patrolPoints: npcConfig.patrolPoints || [],
      seed: SeededRandom.hashString(id)
    });
    character.behavior = behavior;

    // Wire NPC manager reference for group aggro
    behavior._npcManager = this;

    // Set dungeon mode for shorter idle times
    if (npcConfig.dungeonMode) {
      behavior.dungeonMode = true;
    }

    // Combat config: attach CombatStats and configure hostile behavior
    if (npcConfig.combatConfig) {
      character.combatStats = new CombatStats(npcConfig.combatConfig);
      behavior.combatConfig = {
        isHostile: true,
        aggroRange: npcConfig.combatConfig.aggroRange || 8,
        deaggroRange: npcConfig.combatConfig.deaggroRange || 14,
        attackRange: npcConfig.combatConfig.attackRange || 1.8,
        attackCooldown: npcConfig.combatConfig.attackCooldown || 1.0,
        rangedAttackRange: npcConfig.combatConfig.rangedAttackRange || 0,
        projectileType: npcConfig.combatConfig.projectileType || null,
      };
      // References set by DungeonGenerator or main.js after construction
      behavior.combatManager = this._combatManager || null;
      behavior.projectileManager = this._projectileManager || null;
      behavior._audioSystem = this._audioSystem || null;
    }

    // Add nameplate for hostile NPCs showing level, name, and health bar
    if (npcConfig.combatConfig?.isHostile && sprite.element) {
      const level = npcConfig.combatConfig.level || 1;
      const typeName = npcConfig.displayName
        || character.displayName
        || (npcConfig.characterName || 'lpc').charAt(0).toUpperCase() + (npcConfig.characterName || 'lpc').slice(1);
      const nameplate = document.createElement('div');
      nameplate.className = 'npc-nameplate';
      nameplate.innerHTML = `<div class="npc-nameplate-text"><span class="npc-level">Lv.${level}</span> ${typeName}</div><div class="npc-hp-bar"><div class="npc-hp-fill"></div></div>`;
      sprite.element.appendChild(nameplate);
      character._nameplate = nameplate;
      character._nameplateHpFill = nameplate.querySelector('.npc-hp-fill');
      character._npcLevel = level;
    }

    // Make sprite clickable for dialog interaction (skip for hostile NPCs)
    if (!npcConfig.combatConfig || !npcConfig.combatConfig.isHostile) {
      sprite.setInteractive(true);
      if (this.dialogManager) {
        sprite.element.addEventListener('click', (e) => {
          e.stopPropagation();
          this.dialogManager.tryInteract(character);
        });
      }
    }

    // Add to world
    this.world.addCharacter(character);

    // Find ground at or below the clicked position (not from max height,
    // otherwise the NPC snaps to a higher platform at the same x-z)
    // Skip for synced NPCs (behaviorState present) — they already have the
    // correct ground-detected Y from the host peer.
    if (!npcConfig.behaviorState) {
      const groundY = this.groundDetector.findGroundHeight(
        character.x, character.z,
        character.width, character.depth,
        character.y, 1000
      );
      if (groundY !== null) {
        character.y = groundY;
        character.isGrounded = true;
        // Update spawn point Y to actual ground level
        behavior.spawnPoint.y = groundY;
      } else {
        character.isGrounded = true;
      }
    } else {
      character.isGrounded = true;
    }

    // Initialize animator + sprite immediately so NPC is visible on the first frame
    if (character.animator) {
      character.animator.update(0);
    }
    if (character.sprite && this.world.camera) {
      character.sprite.update(this.world.camera);
    }

    this.npcs.set(id, { character, behavior });
    const logName = character.displayName || npcConfig.characterName || 'LPC';
    Debug.log('NPC', `Spawned ${logName} NPC "${id}" at (${character.x.toFixed(1)}, ${character.y.toFixed(1)}, ${character.z.toFixed(1)})`);

    // Broadcast to peers (suppressed during bulk loads and remote ops)
    if (this.onMutation && !this._suppressMutation) {
      const data = {
        id,
        characterName: npcConfig.characterName || 'lpc',
        x: character.x,
        y: character.y,
        z: character.z,
        behaviorType: npcConfig.behaviorType || 'wander',
        wanderRadius: npcConfig.wanderRadius || NPCBehavior.DEFAULT_WANDER_RADIUS,
        patrolPoints: npcConfig.patrolPoints || []
      };
      if (npcConfig.combatConfig) {
        data.combatConfig = npcConfig.combatConfig;
      }
      if (isLPC) {
        data.lpcAppearance = npcConfig.lpcAppearance;
        data.lpcEquipment = npcConfig.lpcEquipment || {};
        data.displayName = npcConfig.displayName || null;
      }
      this.onMutation({ op: 'spawnNPC', data });
    }

    return character;
  }

  /**
   * Resolve equipment slot→itemId map to slot→itemDef map for LPC compositor.
   * @param {Object} equipment - Map of slot → itemId
   * @returns {Object} Map of slot → item definition with lpcLayer
   */
  static _resolveEquipment(equipment) {
    if (!equipment) return {};
    const resolved = {};
    for (const [slot, itemId] of Object.entries(equipment)) {
      const def = ITEM_DEFS[itemId];
      if (def && def.lpcLayer) {
        resolved[slot] = def;
      }
    }
    return resolved;
  }

  /**
   * Remove an NPC by ID
   * @param {string} id
   * @returns {boolean}
   */
  removeNPC(id) {
    const entry = this.npcs.get(id);
    if (!entry) return false;

    this.world.removeCharacter(id);
    this.npcs.delete(id);
    Debug.log('NPC', `Removed NPC "${id}"`);

    // Broadcast to peers (suppressed during bulk loads and remote ops)
    if (this.onMutation && !this._suppressMutation) {
      this.onMutation({ op: 'removeNPC', data: { id } });
    }

    return true;
  }

  /**
   * Remove all NPCs
   */
  removeAll() {
    for (const id of [...this.npcs.keys()]) {
      this.world.removeCharacter(id);
    }
    this.npcs.clear();
  }

  /**
   * Find hostile NPCs within range of a source character (for group aggro).
   * @param {Character} sourceCharacter
   * @param {number} range
   * @returns {Array<{character, behavior}>}
   */
  getHostileNPCsInRange(sourceCharacter, range) {
    const results = [];
    const rangeSq = range * range;
    for (const [id, { character, behavior }] of this.npcs) {
      if (character === sourceCharacter) continue;
      if (!behavior.combatConfig || !behavior.combatConfig.isHostile) continue;
      if (character.combatStats && character.combatStats.isDead) continue;
      const dx = character.x - sourceCharacter.x;
      const dy = character.y - sourceCharacter.y;
      const dz = character.z - sourceCharacter.z;
      if (dx * dx + dy * dy + dz * dz <= rangeSq) {
        results.push({ character, behavior });
      }
    }
    return results;
  }

  /**
   * Get summary list of all NPCs (for UI)
   * @returns {Array<{id, characterName, behaviorType, x, y, z}>}
   */
  getNPCList() {
    const list = [];
    for (const [id, { character, behavior }] of this.npcs) {
      const entry = {
        id,
        characterName: character.characterName,
        behaviorType: behavior.type,
        isHostile: !!(behavior.combatConfig && behavior.combatConfig.isHostile),
        isLPC: !!character.isLPC,
        displayName: character.displayName || null,
        x: character.x,
        y: character.y,
        z: character.z
      };
      list.push(entry);
    }
    return list;
  }

  /**
   * Serialize all NPCs for world save
   * @returns {Array}
   */
  toJSON() {
    const npcs = [];
    for (const [id, { character, behavior }] of this.npcs) {
      // Skip dead NPCs — they should stay dead when the world is restored
      if (character.combatStats && character.combatStats.isDead) continue;

      const npcData = {
        id,
        characterName: character.characterName,
        x: character.x,
        y: character.y,
        z: character.z,
        spawnPoint: { ...behavior.spawnPoint },
        behaviorType: behavior.type,
        wanderRadius: behavior.wanderRadius,
        patrolPoints: behavior.patrolPoints.map(p => ({ x: p.x, y: p.y, z: p.z })),
        behaviorState: behavior.getState()
      };
      // Display name (both LPC and legacy)
      if (character.displayName) npcData.displayName = character.displayName;
      // Serialize LPC appearance and equipment
      if (character.isLPC) {
        npcData.lpcAppearance = character.lpcAppearance;
        npcData.lpcEquipment = character.lpcEquipment;
      }
      if (behavior.combatConfig && behavior.combatConfig.isHostile) {
        const cs = character.combatStats;
        npcData.combatConfig = {
          isHostile: true,
          aggroRange: behavior.combatConfig.aggroRange,
          deaggroRange: behavior.combatConfig.deaggroRange,
          attackRange: behavior.combatConfig.attackRange,
          attackCooldown: behavior.combatConfig.attackCooldown,
          level: cs?.level || 1,
          maxHealth: cs?.maxHealth,
          attackPower: cs?.attackPower,
          defense: cs?.defense,
          maxMana: cs?.maxMana || 50,
          maxStamina: cs?.maxStamina || 100,
          manaRegen: cs?.manaRegen || 1.0,
          healthRegen: cs?.healthRegen || 0.5,
          meleeDamageBonus: cs?.meleeDamageBonus || 0,
          rangedDamageBonus: cs?.rangedDamageBonus || 0,
          spellDamageBonus: cs?.spellDamageBonus || 0,
          archetype: cs?.archetype || null,
          lootTable: cs?.lootTable,
          isBoss: cs?.isBoss || false,
          rangedAttackRange: behavior.combatConfig.rangedAttackRange || 0,
          projectileType: behavior.combatConfig.projectileType || null
        };
      }
      npcs.push(npcData);
    }
    return npcs;
  }

  /**
   * Deserialize and spawn NPCs from world data
   * @param {Array} npcArray
   */
  async fromJSON(npcArray) {
    this.removeAll();
    if (!npcArray || npcArray.length === 0) return;

    for (const config of npcArray) {
      try {
        const character = await this.spawnNPC(config);
        // Restore behavior runtime state (multiplayer sync / saved worlds)
        if (config.behaviorState && character.behavior) {
          character.behavior.setState(config.behaviorState);
        }
      } catch (error) {
        Debug.error('NPC', `Failed to spawn NPC "${config.id}":`, error);
      }
    }
  }
}
